// Club progression levels — defines the path from empty lot to elite club

export interface ClubLevelRequirement {
  type: 'courts' | 'buildings' | 'staff' | 'reputation' | 'services' | 'upgrades' | 'members'
    // Fase 2 — the league finally feeds club progression:
    | 'league_division' // be in division <= value (1 is Primera)
    | 'facility_level'  // have `count` facilities at level >= value
  value: number
  /** Only for 'facility_level': how many facilities must reach `value` */
  count?: number
  label: string
}

export interface ClubLevel {
  level: number
  name: string
  emoji: string
  description: string
  requirements: ClubLevelRequirement[]
  unlocks: string[]           // Feature IDs unlocked at this level
  unlockDescriptions: string[] // Human-readable unlock descriptions
  reward: {
    money: number
    reputation: number
  }
}

// Feature IDs used for gating
export const FEATURES = {
  // Courts
  BUILD_OUTDOOR: 'build_outdoor',
  BUILD_INDOOR: 'build_indoor',
  // Buildings
  BUILD_RECEPTION: 'build_reception',
  BUILD_SHOP: 'build_shop',
  BUILD_LOCKER: 'build_locker',
  BUILD_CAFE: 'build_cafe',
  // Staff
  HIRE_COACH: 'hire_coach',
  HIRE_RECEPTIONIST: 'hire_receptionist',
  HIRE_MAINTENANCE: 'hire_maintenance',
  HIRE_BARISTA: 'hire_barista',
  HIRE_DOCTOR: 'hire_doctor',
  HIRE_PHYSIO: 'hire_physio',
  HIRE_PSYCHOLOGIST: 'hire_psychologist',
  HIRE_NUTRITIONIST: 'hire_nutritionist',
  // Services
  SERVICE_COURT_RENTAL: 'service_court_rental',
  SERVICE_GROUP_CLASSES: 'service_group_classes',
  SERVICE_KIDS_CAMP: 'service_kids_camp',
  SERVICE_PRIVATE_EVENTS: 'service_private_events',
  SERVICE_PRO_SHOP: 'service_pro_shop',
  SERVICE_CAFETERIA: 'service_cafeteria',
  // Economy
  MEMBERSHIPS_BASIC: 'memberships_basic',
  MEMBERSHIPS_STANDARD: 'memberships_standard',
  MEMBERSHIPS_PREMIUM: 'memberships_premium',
  MEMBERSHIPS_VIP: 'memberships_vip',
  SPONSORSHIPS: 'sponsorships',
  LOANS: 'loans',
  // Competitions
  TOURNAMENTS_AMATEUR: 'tournaments_amateur',
  TOURNAMENTS_INTERMEDIATE: 'tournaments_intermediate',
  TOURNAMENTS_PROFESSIONAL: 'tournaments_professional',
  TOURNAMENTS_CHAMPIONSHIP: 'tournaments_championship',
  // Other
  COURT_UPGRADES: 'court_upgrades',
  ACADEMY: 'academy',
  PLAYER_ROSTER: 'player_roster',
  LEAGUE: 'league',
} as const

export const CLUB_LEVELS: ClubLevel[] = [
  {
    level: 0,
    name: 'Terreno Baldio',
    emoji: '🏜️',
    description: 'Un solar vacio con mucho potencial',
    requirements: [],
    unlocks: [FEATURES.BUILD_OUTDOOR, FEATURES.LOANS],
    unlockDescriptions: ['Construir pistas outdoor', 'Solicitar prestamos'],
    reward: { money: 0, reputation: 0 },
  },
  {
    level: 1,
    name: 'Pista de Barrio',
    emoji: '🎾',
    description: 'Tu primera pista esta lista para recibir jugadores',
    requirements: [
      { type: 'courts', value: 1, label: '1 pista construida' },
    ],
    unlocks: [
      FEATURES.SERVICE_COURT_RENTAL,
      FEATURES.BUILD_RECEPTION,
    ],
    unlockDescriptions: [
      'Alquiler de pistas',
      'Construir recepcion',
    ],
    reward: { money: 1000, reputation: 0.2 },
  },
  {
    level: 2,
    name: 'Club Incipiente',
    emoji: '🏗️',
    description: 'El club empieza a tomar forma',
    requirements: [
      { type: 'courts', value: 2, label: '2 pistas' },
      { type: 'buildings', value: 1, label: '1 edificio' },
    ],
    unlocks: [
      FEATURES.HIRE_COACH,
      FEATURES.HIRE_RECEPTIONIST,
      FEATURES.HIRE_MAINTENANCE,
      FEATURES.BUILD_SHOP,
      FEATURES.BUILD_LOCKER,
      FEATURES.MEMBERSHIPS_BASIC,
      FEATURES.SERVICE_GROUP_CLASSES,
    ],
    unlockDescriptions: [
      'Contratar personal',
      'Construir Pro Shop y Vestuarios',
      'Membresias basicas',
      'Clases grupales',
    ],
    reward: { money: 2500, reputation: 0.3 },
  },
  {
    level: 3,
    name: 'Club Establecido',
    emoji: '🏟️',
    description: 'Un club reconocido en el barrio',
    requirements: [
      { type: 'courts', value: 3, label: '3 pistas' },
      { type: 'buildings', value: 2, label: '2 edificios' },
      { type: 'staff', value: 2, label: '2 empleados' },
    ],
    unlocks: [
      FEATURES.BUILD_CAFE,
      FEATURES.BUILD_INDOOR,
      FEATURES.HIRE_BARISTA,
      FEATURES.ACADEMY,
      FEATURES.TOURNAMENTS_AMATEUR,
      FEATURES.MEMBERSHIPS_STANDARD,
      FEATURES.SERVICE_KIDS_CAMP,
      FEATURES.SERVICE_PRO_SHOP,
      FEATURES.SERVICE_CAFETERIA,
      FEATURES.PLAYER_ROSTER,
      FEATURES.LEAGUE,
    ],
    unlockDescriptions: [
      'Pistas indoor',
      'Cafeteria y barista',
      'Academia de padel',
      'Torneos amateur',
      'Membresias standard',
      'Campus infantil, Pro Shop, Cafeteria',
      'Plantilla de jugadores',
      'Liga regional de clubes',
    ],
    reward: { money: 5000, reputation: 0.5 },
  },
  {
    level: 4,
    name: 'Club Reconocido',
    emoji: '⭐',
    description: 'Tu club tiene buena reputacion en la ciudad',
    requirements: [
      { type: 'courts', value: 4, label: '4 pistas' },
      { type: 'buildings', value: 3, label: '3 edificios' },
      { type: 'staff', value: 3, label: '3 empleados' },
      { type: 'reputation', value: 3.5, label: 'Reputacion 3.5+' },
    ],
    unlocks: [
      FEATURES.COURT_UPGRADES,
      FEATURES.MEMBERSHIPS_PREMIUM,
      FEATURES.SPONSORSHIPS,
      FEATURES.SERVICE_PRIVATE_EVENTS,
      FEATURES.TOURNAMENTS_INTERMEDIATE,
      FEATURES.HIRE_DOCTOR,
      FEATURES.HIRE_PHYSIO,
    ],
    unlockDescriptions: [
      'Mejoras de pistas',
      'Membresias premium',
      'Patrocinios',
      'Eventos privados',
      'Torneos intermedios',
      'Cuerpo medico (medico y fisio)',
    ],
    reward: { money: 8000, reputation: 0.5 },
  },
  {
    level: 5,
    name: 'Club de Referencia',
    emoji: '🌟',
    description: 'Referente del padel en la region',
    requirements: [
      { type: 'courts', value: 5, label: '5 pistas' },
      { type: 'upgrades', value: 3, label: '3 mejoras instaladas' },
      { type: 'reputation', value: 4.0, label: 'Reputacion 4.0+' },
      { type: 'members', value: 30, label: '30 socios' },
    ],
    unlocks: [
      FEATURES.MEMBERSHIPS_VIP,
      FEATURES.TOURNAMENTS_PROFESSIONAL,
    ],
    unlockDescriptions: [
      'Membresias VIP',
      'Torneos profesionales',
    ],
    reward: { money: 12000, reputation: 0.5 },
  },
  {
    level: 6,
    name: 'Centro Deportivo',
    emoji: '🏆',
    description: 'Un centro deportivo de primer nivel',
    requirements: [
      { type: 'courts', value: 6, label: '6 pistas' },
      { type: 'staff', value: 5, label: '5 empleados' },
      { type: 'reputation', value: 4.5, label: 'Reputacion 4.5+' },
      { type: 'members', value: 60, label: '60 socios' },
    ],
    unlocks: [
      FEATURES.TOURNAMENTS_CHAMPIONSHIP,
      FEATURES.HIRE_PSYCHOLOGIST,
      FEATURES.HIRE_NUTRITIONIST,
    ],
    unlockDescriptions: [
      'Torneos championship',
      'Psicologo y nutricionista',
    ],
    reward: { money: 20000, reputation: 0.5 },
  },
  {
    level: 7,
    name: 'Club de Elite',
    emoji: '👑',
    description: 'El mejor club de padel de la region',
    requirements: [
      { type: 'courts', value: 8, label: '8 pistas' },
      { type: 'reputation', value: 4.8, label: 'Reputacion 4.8+' },
      { type: 'members', value: 100, label: '100 socios' },
      { type: 'services', value: 5, label: '5 servicios activos' },
    ],
    unlocks: [],
    unlockDescriptions: ['Ampliaciones de instalaciones de nivel 8'],
    reward: { money: 50000, reputation: 0.5 },
  },
  {
    level: 8,
    name: 'Club Nacional',
    emoji: '🏛️',
    description: 'Un referente del padel a escala nacional',
    requirements: [
      { type: 'courts', value: 10, label: '10 pistas' },
      { type: 'reputation', value: 4.8, label: 'Reputacion 4.8+' },
      { type: 'members', value: 150, label: '150 socios' },
      { type: 'league_division', value: 2, label: 'Competir en 2ª División o superior' },
      { type: 'facility_level', value: 5, count: 2, label: '2 instalaciones a nivel 5+' },
    ],
    unlocks: [],
    unlockDescriptions: ['Ampliaciones de instalaciones de nivel 9'],
    reward: { money: 75000, reputation: 0.5 },
  },
  {
    level: 9,
    name: 'Icono del Padel',
    emoji: '🌐',
    description: 'El club que todo el mundo del padel conoce',
    requirements: [
      { type: 'courts', value: 12, label: '12 pistas' },
      { type: 'members', value: 250, label: '250 socios' },
      { type: 'league_division', value: 1, label: 'Competir en 1ª División' },
      { type: 'facility_level', value: 7, count: 4, label: '4 instalaciones a nivel 7+' },
    ],
    unlocks: [],
    unlockDescriptions: ['Todo desbloqueado - Modo sandbox'],
    reward: { money: 150000, reputation: 0.5 },
  },
]

/** Get all features unlocked up to a given level */
export function getUnlockedFeatures(level: number): string[] {
  const features: string[] = []
  for (const cl of CLUB_LEVELS) {
    if (cl.level <= level) {
      features.push(...cl.unlocks)
    }
  }
  return features
}

/** Get the next level definition (or null if max) */
export function getNextLevel(currentLevel: number): ClubLevel | null {
  return CLUB_LEVELS.find(l => l.level === currentLevel + 1) ?? null
}

/** Check if a feature is unlocked at a given level */
export function isFeatureUnlocked(feature: string, level: number): boolean {
  return getUnlockedFeatures(level).includes(feature)
}
